import type { OpaqueVideoData } from '@lumen5/luminary';
import Luminary from '@lumen5/luminary';

import { NO_VIDEO_STATE_MESSAGE } from './constants';
import {
    captureVideoState,
    computeVideoDiff,
    formatInitialStateAsGitDiff,
    formatVideoDiffAsGitPatch,
} from './diff';
import type { VideoStateSnapshot } from './types';

import { IconRegistry } from 'js/src/agent/registries/IconRegistry';
import { MediaRegistry } from 'js/src/agent/registries/MediaRegistry';
import { MusicRegistry } from 'js/src/agent/registries/MusicRegistry';
import type { SceneAddressing } from 'js/src/agent/tools/LintVideoTool';
import type { MediaCandidates } from 'js/src/client';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';

/**
 * Options for capturing video state context
 */
export type CaptureVideoStateContextOptions = {
    mediaCandidates?: MediaCandidates | null;
    previousState?: VideoStateSnapshot | null;
    useDiffMode?: boolean;
    /**
     * Overrides the "## Video Context" heading of the full-state description.
     * Used when the serialized video is NOT the video being edited (e.g. a
     * blueprint's reference video), where the default heading would mislead
     * the agent into treating it as the current video state.
     */
    header?: string;
    // Redux accessor, when available. Resolved once per capture to enrich the snapshot with
    // per-scene transcript coverage AND transcript pauses (SilenceNodes live in the Lexical
    // editor, not in Luminary). Optional so storeless callers degrade to pause-less, coverage-less
    // transcripts.
    getState?: GetRootState;
    /** Addressing mode for the serialized state: stable scene_id/scene_group_id handles, or positional index. Defaults to 'index'; the Toolbox uses 'scene_id'. */
    sceneAddressing?: SceneAddressing;
    /**
     * Whether serialized colors carry a `(name)` alongside the hex (e.g. `#ff0000 (Red)` vs bare
     * `#ff0000`). Defaults to true; the Toolbox turns it off since its model reads hex directly.
     * Baked into the snapshot at capture, so every capture destined for the same store must agree
     * on this (mixing named and unnamed snapshots would produce phantom color diffs).
     */
    includeColorNames?: boolean;
};

/**
 * Result of capturing video state context
 */
export type CaptureVideoStateContextResult = {
    description: string;
    snapshot: VideoStateSnapshot;
    isDiff: boolean;
    hasChanges: boolean;
};

/**
 * Captures the current video state and generates a text-based description for agent context.
 * @param luminary The Luminary instance containing video data
 * @param options Configuration options including media candidates and previous state for diff mode
 */
export async function captureVideoStateContext(
    luminary: Luminary,
    options?: CaptureVideoStateContextOptions,
): Promise<CaptureVideoStateContextResult> {
    const {
        mediaCandidates,
        previousState,
        useDiffMode = true,
        header,
        getState,
        sceneAddressing = 'index',
        includeColorNames = true,
    } = options || {};

    // Ensure all assets from the video are in the registry
    const mediaRegistry = MediaRegistry.getInstance();
    await mediaRegistry.addAnyUnseenItemsFromVideo(luminary);
    const iconRegistry = IconRegistry.getInstance();
    await iconRegistry.addAnyUnseenItemsFromVideo(luminary);
    const musicRegistry = MusicRegistry.getInstance();
    await musicRegistry.addAnyUnseenItemsFromVideo(luminary);

    // Add media candidates from recommendations to registries
    if (mediaCandidates) {
        await mediaRegistry.addCandidatesFromRecommendations(mediaCandidates);
        await iconRegistry.addCandidatesFromRecommendations(mediaCandidates);
    }

    // Capture current state
    const currentSnapshot = await captureVideoState(
        luminary,
        mediaCandidates,
        getState,
        includeColorNames,
    );

    // Generate diff if previous state exists and diff mode is enabled
    if (useDiffMode && previousState) {
        const diff = computeVideoDiff(previousState, currentSnapshot);
        const gitDiff = formatVideoDiffAsGitPatch(diff, undefined, sceneAddressing);
        const hasChanges = gitDiff !== '';
        const diffDescription = formatVideoDiff(diff, sceneAddressing);

        return {
            description: diffDescription,
            snapshot: currentSnapshot,
            isDiff: true,
            hasChanges,
        };
    }

    // No video has been created yet (no scenes), so there is no state to report.
    if (currentSnapshot.video.sceneCount === 0) {
        return {
            description: NO_VIDEO_STATE_MESSAGE,
            snapshot: currentSnapshot,
            isDiff: false,
            hasChanges: false,
        };
    }

    // Generate full description using git diff format (first call or diff mode disabled)
    let description = header ?? '## Video Context\n_This is the complete video state_\n\n';

    // Use git diff format for initial state
    description += formatInitialStateAsGitDiff(currentSnapshot, sceneAddressing);

    return {
        description,
        snapshot: currentSnapshot,
        isDiff: false,
        hasChanges: true, // Initial full state always has "changes" to report
    };
}

/**
 * Format a video diff into a human-readable description using git patch format
 */
function formatVideoDiff(
    diff: ReturnType<typeof computeVideoDiff>,
    sceneAddressing: SceneAddressing,
): string {
    let description = '## Video Context\n';
    description += `_This is an incremental update showing only changes since ${new Date(diff.baselineTimestamp).toISOString()}_\n\n`;

    // Use git patch format for the diff
    const gitDiff = formatVideoDiffAsGitPatch(diff, undefined, sceneAddressing);

    if (gitDiff) {
        description += gitDiff;
    } else {
        description += '_(No changes detected)_\n';
    }

    return description;
}

/**
 * Generates a comprehensive video description from video data by creating a temporary Luminary instance.
 * This is useful for analyzing videos without having an existing Luminary instance.
 * @param videoData The video data to analyze
 * @param options.getState Optional accessor for a state carrying this video's transcript editors
 *   (and templates). When provided, the description includes per-scene coverage and transcript
 *   pauses — the same output get_video_state produces. Omit it for a structure-only description.
 * @returns A formatted string description of the video including metadata, music, and scene details
 */
export async function generateVideoDescriptionFromVideoData(
    videoData: OpaqueVideoData,
    options?: Pick<CaptureVideoStateContextOptions, 'header' | 'getState'>,
): Promise<string> {
    // Create temporary Luminary instance for analysis
    const localLuminary = new Luminary({
        fetchResources: true,
        disableRenderer: true,
        autoDraw: true,
        autoUpdate: true,
    });

    try {
        localLuminary.load(videoData);
        await localLuminary.onAssetsLoaded();

        const result = await captureVideoStateContext(localLuminary, {
            header: options?.header,
            getState: options?.getState,
        });
        return result.description;
    } finally {
        // Always clean up the local Luminary instance
        localLuminary.destroy();
    }
}
